import React from "react";

import { Metric } from "~/models/metric";
import { prop } from "~/tools/prop";

import { type InstantPromQLResult, queryInstantPromQL } from "../../../../queries/metrics/batch";
import { useQuery } from "../../../../queries/react-query";

import { ListMultiChoiceVariable, type ListMultiChoiceVariableProps } from "./_list-multi-choice";
import { ListSingleChoiceVariable, type ListSingleChoiceVariableProps } from "./_list-single-choice";

type MultiChoiceProps = {
	allowMultiple: true;
} & Omit<ListMultiChoiceVariableProps, "options">;

type SingleChoiceProps = { allowMultiple: false } & Omit<ListSingleChoiceVariableProps, "options">;

type Props = {
	expression: string;
	label: string;
} & (MultiChoiceProps | SingleChoiceProps);

export const MetricsPromQLVariable = ({ expression, label, ...props }: Props) => {
	const [dropdownVisible, setDropdownVisible] = React.useState(false);

	const { data: options = [] } = useQuery({
		enabled: dropdownVisible && expression.length > 0,
		keepPreviousData: true,

		select: React.useCallback(
			(result: InstantPromQLResult) =>
				result
					.transform((vectors) => Metric.extractLabelValuesByName(label, vectors.map(prop("metric"))))
					.getOrElse([])
					.map((value) => ({ value, label: value })),
			[label],
		),

		...queryInstantPromQL({ step: undefined, query: expression }),
	});

	if (props.allowMultiple) {
		return <ListMultiChoiceVariable {...props} onDropdownVisibleChange={setDropdownVisible} options={options} />;
	}

	return <ListSingleChoiceVariable {...props} onDropdownVisibleChange={setDropdownVisible} options={options} />;
};
